import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import {
  FormControl,
  FormRecord,
  FormsModule,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzListModule } from 'ng-zorro-antd/list';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTableModule } from 'ng-zorro-antd/table';
import { take } from 'rxjs';
import { ColorSelectorXComponent } from '../../../basic/color/color-selector-x/color-selector-x.component';
import { Personality, PersonalityUnSafeSubData } from '../../../core/personality/personality';
import { PersonalityService } from '../../../core/personality/personality.service';
import { AppColor, AppColorName } from '../../../shared/color/color';
import { EnumUtils } from '../../../shared/utils/enum-utils';

@Component({
  selector: 'app-new-edit-personality',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,

    NzIconModule,
    NzInputModule,
    NzButtonModule,
    NzTableModule,
    NzPopoverModule,
    NzModalModule,
    NzAlertModule,
    NzFormModule,
    NzListModule,
    NzSelectModule,
    NzPopconfirmModule,

    ColorSelectorXComponent,
  ],
  templateUrl: './new-edit-personality.component.html',
  styleUrl: './new-edit-personality.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewEditPersonalityComponent {
  @Input() selectedItem?: Personality;
  @Input() isNewEditVisible = false;

  @Output() isNewEditVisibleChange = new EventEmitter<boolean>();

  AppColor = AppColor;

  validateForm: FormRecord<FormControl<string>>;

  constructor(
    private personalityService: PersonalityService,
    private fb: NonNullableFormBuilder
  ) {
    this.validateForm = this.fb.group({
      title: ['', [Validators.required]],
      color: [AppColor.orange, [Validators.required]],
      personality: [''],
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    for (const propName in changes) {
      const chng = changes[propName];
      if (propName === 'selectedItem') {
        if (chng.previousValue !== chng.currentValue) {
          const selectedItem = chng.currentValue;
          this.validateForm.patchValue({
            title: selectedItem.title ?? '',
            color: EnumUtils.getEnumValueByEnumKey(AppColor, selectedItem.unsafeSubData?.color) ?? AppColor.orange,
            personality: selectedItem.description ?? '',
          });
        }
      }
    }

    if (!this.selectedItem) {
      this.validateForm = this.fb.group({
        title: ['', [Validators.required]],
        color: [AppColor.orange, [Validators.required]],
        personality: [''],
      });
    }
  }

  onDeleteClick() {
    if (this.selectedItem) {
      this.personalityService.delete$([this.selectedItem!]).pipe(take(1)).subscribe();
    }

    this.isNewEditVisible = false;
    this.isNewEditVisibleChange.emit(this.isNewEditVisible);
  }

  submitForm() {
    if (this.validateForm.valid) {
      this.isNewEditVisible = false;
      this.isNewEditVisibleChange.emit(false);

      const title = this.validateForm.value['title'] ?? 'Default Tag';
      const color = EnumUtils.getEnumKeyByEnumValue(AppColor, this.validateForm.value['color']) ?? AppColorName.orange;

      const description = this.validateForm.value['personality'] ?? '';

      const subData: PersonalityUnSafeSubData = { color };

      if (this.selectedItem) {
        this.selectedItem.title = title;
        this.selectedItem.description = description;
        this.selectedItem.unsafeSubData = subData;

        this.personalityService.update$(this.selectedItem!).pipe(take(1)).subscribe();
      } else {
        this.personalityService
          .add$(title ?? '', description, subData)
          .pipe(take(1))
          .subscribe();
      }
    } else {
      Object.values(this.validateForm.controls).forEach((control) => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
    }
  }

  onIsNewEditVisibleChange(event: boolean) {
    this.isNewEditVisibleChange.emit(event);
  }

  onCancelClick() {
    this.isNewEditVisible = false;

    this.isNewEditVisibleChange.emit(false);
  }
}
